import { useState } from 'react'

import uniq from 'lodash/uniq'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { type Return } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { DaysByRow } from 'modules/shift_management/hooks/reducers/selection'
import { ShiftsAttributesByDate } from 'modules/shift_management/hooks/useDefaultAttributesFromRows'
import { useVisibleDefaultShifts } from 'modules/shift_management/hooks/useVisibleDefaultShifts'
import DefaultShiftWithParamsForm from 'modules/shift_management/lib/forms/DefaultShiftWithParamsForm'
import { trackEditShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import shiftsCollection from 'modules/shift_management/store/api/collections/shifts'
import type DefaultShift from 'modules/shift_management/store/internal/models/DefaultShift'
import type Shift from 'modules/shift_management/store/internal/models/Shift'

import { useMutateDefaultShifts } from './useMutateDefaultShifts'
import { useSchedule } from './useSchedule'
import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'

interface ReassignShiftProps {
  shifts: Shift[]
  defaultAttributes: ShiftsAttributesByDate
  daysByRow: DaysByRow
  preselectedSavedShifts: (number | null)[]
  isOriginalIncluded: boolean
  showReassignMessage: boolean
  contractVersions: ContractVersions
}

export interface ReturnProps {
  forms: DefaultShiftWithParamsForm[]
  onToggleShift: (id: string) => void
  onSubmit: () => Promise<Return<void>>
  submitting: boolean
}

const useReplaceSavedShifts = ({
  shifts,
  defaultAttributes,
  daysByRow,
  preselectedSavedShifts,
  isOriginalIncluded,
  showReassignMessage,
  contractVersions,
}: ReassignShiftProps): ReturnProps => {
  const [, dispatchShiftsRows] = useShiftsRows()
  const dispatchSelection = useSelectionDispatch()
  const [{ view }] = useSchedule()

  const visibleDefaultShifts = useVisibleDefaultShifts({})
  const onlyOnePreselectedSavedShift = preselectedSavedShifts.length === 1

  const preselectSavedShift = (shiftId: number): boolean => {
    if (!onlyOnePreselectedSavedShift) return false
    return preselectedSavedShifts.includes(shiftId)
  }

  const defaultSavedShift = onlyOnePreselectedSavedShift ? shifts[0] : null

  const initializeForms = () => {
    return visibleDefaultShifts.map((defaultShift: DefaultShift) => {
      const { id, title, startAt, endAt, overnightShift, color, locationId, workAreaId, notes } =
        defaultShift

      const isSelected = preselectSavedShift(id) && !!defaultSavedShift

      return new DefaultShiftWithParamsForm({
        id,
        title,
        start_at: startAt,
        end_at: endAt,
        extra_hours: isSelected ? defaultSavedShift.extraHours : false,
        color,
        overnight_shift: overnightShift,
        location_id: isSelected ? defaultSavedShift.locationId : locationId,
        work_area_id: isSelected ? defaultSavedShift.workAreaId : workAreaId,
        notes: isSelected ? defaultSavedShift.notes : notes,
        is_selected: isSelected,
      })
    })
  }

  const [forms] = useState<DefaultShiftWithParamsForm[]>(initializeForms())
  const selectedShiftForms = forms.filter((form) => form.get('is_selected').value)
  const selectedShiftFormsNotes = uniq(selectedShiftForms.map((form) => form.get('notes').value))
  const selectedShiftFormsIds = new Set(selectedShiftForms.map((form) => form.get('id').value))

  const isOnlyEditingShifts =
    preselectedSavedShifts.length === selectedShiftFormsIds.size &&
    preselectedSavedShifts.every((id) => selectedShiftFormsIds.has(id?.toString()))

  const totalNumberOfAssignedEmployees = uniq(Object.keys(daysByRow)).length

  const onlyOneSelectedForm = selectedShiftForms?.length === 1

  const { onSubmit: onSubmitAdd } = useMutateDefaultShifts({
    defaultAttributes,
    daysByRow,
    forms: selectedShiftForms,
    weekDays: '',
    replaceShifts: true,
    contractVersions,
  })

  const onToggleShift = (id: string) => {
    const form = forms.find((f) => f.get('id').value === id)

    if (!form) throw 'Could not find form'

    form.get('is_selected').set(!form.get('is_selected').value)
  }

  const { running: submitting, performAction } = useAsyncAction(
    async () => {
      const shiftsIds = shifts.map((shift) => shift.id)
      await shiftsCollection.rpc('bulk_destroy', { ids: shiftsIds })
      await dispatchShiftsRows({ type: 'DESTROY_SHIFTS', shifts, view })
      onSubmitAdd()

      dispatchSelection({
        type: 'CLEAR_SELECTION',
      })

      await trackEditShiftSubmit({
        shifts: shifts,
        name: onlyOneSelectedForm ? selectedShiftForms[0]?.get('title').value : null,
        startAt: onlyOneSelectedForm ? selectedShiftForms[0]?.get('start_at').value : null,
        endAt: onlyOneSelectedForm ? selectedShiftForms[0]?.get('end_at').value : null,
        notes: onlyOneSelectedForm ? selectedShiftForms[0]?.get('notes').value : null,
        extraHours: onlyOneSelectedForm ? selectedShiftForms[0]?.get('extra_hours').value : null,
        locationId: onlyOneSelectedForm ? selectedShiftForms[0]?.get('location_id').value : null,
        workAreaId: onlyOneSelectedForm ? selectedShiftForms[0]?.get('work_area_id').value : null,
        mixedValueNotes: selectedShiftFormsNotes.length > 1 ? selectedShiftFormsNotes.length : 0,
        view,
        assignedToModified: showReassignMessage,
        originalEmployeeIncluded: isOriginalIncluded,
        totalNumberOfAssignedEmployees: totalNumberOfAssignedEmployees,
        through: 'side_panel',
        replacedShifts: !isOnlyEditingShifts,
      })
    },
    {
      successMessage: i18n.t('shift_management.actions.edit_shifts_success'),
    }
  )

  return {
    forms,
    onToggleShift,
    onSubmit: performAction,
    submitting,
  }
}

export default useReplaceSavedShifts
